import {
  getGetNodoPortaleQueryKey,
  NodoDto,
  useActionDispositivo,
  useGetRegistriDispositivo,
} from "@/api";
import Button from "@/elements/Button";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { SmallLoading } from "@/components/Loading";
import { portaleConfig } from "@/portaliConfig";
import { Box, Grid } from "@mui/material";
import { getMascheraDispositivoHelper } from "..";

export type GFL_TLV_300_Action =
  | "start"
  | "stop"
  | "start p fluss"
  | "stop p fluss"
  | "start p ricircolo"
  | "stop p ricircolo"
  | "reset"
  | "start p contapar"
  | "stop p contapar";

export type GFL_TLV_300_Status =
  | "off"
  | "stop"
  | "alarm"
  | "on-flus"
  | "on-flusric"
  | "on-fluscont"
  | "on-ric";

export function GFL_TLV_300_Buttons({ nodo }: { nodo: NodoDto }) {
  const { t } = useTranslation();
  const [sentAction, setSentAction] = useState<GFL_TLV_300_Action | null>(null);

  const { mutateAsync: setActionAsync, isLoading: actionLoading } =
    useActionDispositivo();

  const queryClient = useQueryClient();

  async function setAction(action: GFL_TLV_300_Action) {
    try {
      setSentAction(action);
      const response = await setActionAsync({
        id: nodo.id,
        data: { action: action },
      });
      if (response.data?.updated) {
        toast.success(t("dispositivoActions.toast.success"));
      } else {
        toast.warn(t("dispositivoActions.toast.noResponse"));
      }
    } catch (e) {
      toast.error(t("dispositivoActions.toast.error"));
    } finally {
      setSentAction(null);
      queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo.id),
      });
    }
  }

  const { data: registriData } = useGetRegistriDispositivo(nodo.id);
  const helper = nodo?.dispositivoModello?.maschera
    ? getMascheraDispositivoHelper(nodo.dispositivoModello.maschera)
    : null;

  const registriValuesMap = useMemo(() => {
    if (!registriData || !helper || !('registriToForm' in helper)) {
      return null;
    }

    return helper.registriToForm(registriData.data);
  }, [helper, registriData]);

  const status: GFL_TLV_300_Status | null =
    (nodo.dispositivoLastStatus?.trim() ?? null) as GFL_TLV_300_Status | null;

  // const canStart = status === "stop";
  // const canStop = status === "on";
  const canReset = status === "alarm";

  const flussRunning =
    status === "on-flus" || status === "on-fluscont" || status === "on-flusric";
  const ricRunning = status === "on-ric" || status === "on-flusric";
  const contRunning = status === "on-fluscont";

  const canStartFluss =
    !flussRunning &&
    (status === "stop" || ricRunning) &&
    parseInt(registriValuesMap?.["funzmanpower"]) > 0;
  const canStopFluss = flussRunning;

  const canStartRic =
    !ricRunning && (status === "stop" || flussRunning) && !contRunning;
  const canStopRic = ricRunning;

  const canStartCont = !contRunning && !ricRunning && flussRunning;
  const canStopCont = contRunning;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {/* Controlli modalità automatica */}
      <Grid container spacing={1}>
        {/* <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStart}
            onClick={() => setAction("start")}
            startIcon={
              sentAction === "start" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            Start
          </Button>
        </Grid> */}
        {/* <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStop}
            onClick={() => setAction("stop")}
            startIcon={sentAction === "stop" && <SmallLoading sx={{ mr: 1 }} />}
            fullWidth
          >
            Stop
          </Button>
        </Grid> */}
        <Grid item xs={4}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canReset}
            onClick={() => setAction("reset")}
            startIcon={
              sentAction === "reset" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("azioni.reset")}
          </Button>
        </Grid>
      </Grid>

      {/* Controlli pompe flussaggio e ricircolo */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStartFluss}
            onClick={() => setAction("start p fluss")}
            startIcon={
              sentAction === "start p fluss" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("gflTlv300.buttons.startPompaFlussaggio")}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStopFluss}
            onClick={() => setAction("stop p fluss")}
            startIcon={
              sentAction === "stop p fluss" && <SmallLoading sx={{ mr: 1 }} />
            }
            fullWidth
          >
            {t("gflTlv300.buttons.stopPompaFlussaggio")}
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStartRic}
            onClick={() => setAction("start p ricircolo")}
            startIcon={
              sentAction === "start p ricircolo" && (
                <SmallLoading sx={{ mr: 1 }} />
              )
            }
            fullWidth
          >
            {t("gflTlv300.buttons.startPompaRicircolo")}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStopRic}
            onClick={() => setAction("stop p ricircolo")}
            startIcon={
              sentAction === "stop p ricircolo" && (
                <SmallLoading sx={{ mr: 1 }} />
              )
            }
            fullWidth
          >
            {t("gflTlv300.buttons.stopPompaRicircolo")}
          </Button>
        </Grid>
      </Grid>

      {/* Reset e pompa contaparticelle */}
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStartCont}
            onClick={() => setAction("start p contapar")}
            startIcon={
              sentAction === "start p contapar" && (
                <SmallLoading sx={{ mr: 1 }} />
              )
            }
            fullWidth
          >
            {t("gflTlv300.buttons.startContaparticelle")}
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button
            size="small"
            variant="contained"
            disabled={actionLoading || !canStopCont}
            onClick={() => setAction("stop p contapar")}
            startIcon={
              sentAction === "stop p contapar" && (
                <SmallLoading sx={{ mr: 1 }} />
              )
            }
            fullWidth
          >
            {t("gflTlv300.buttons.stopContaparticelle")}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
